// Global subscription manager to prevent duplicate subscriptions and handle visibility changes

// Delay before unsubscribing when no more hooks are subscribed (allows for rapid re-subscriptions)
const UNSUBSCRIBE_DEBOUNCE_MS = 100

type SubscriptionHandler = () => void
type UnsubscriptionHandler = () => void

interface PendingSubscription {
  eventList: string
  subscribeFn: SubscriptionHandler
}

class WebSocketSubscriptionManager {
  private activeSubscriptions = new Map<string, Set<string>>()
  private pendingUnsubscriptions = new Map<string, NodeJS.Timeout>()
  private pendingSubscriptions: PendingSubscription[] = []
  private isPageVisible = true // Default to true for SSR
  private isLoggedIn = false
  private isInitialized = false
  private visibilityHandler?: () => void
  private onVisibilityChange?: (visible: boolean) => void
  private onReconnectNeeded?: () => void

  constructor() {
    // Only initialize on client side
    if (typeof window !== 'undefined') {
      this.initializeClient()
    }
  }

  private initializeClient() {
    if (this.isInitialized) return

    this.isPageVisible = !document.hidden
    this.isInitialized = true

    // Store the handler so we can remove it later if needed
    this.visibilityHandler = this.handleVisibilityChange.bind(this)

    // Listen for visibility changes globally
    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  private handleVisibilityChange() {
    if (typeof window === 'undefined') return

    const wasVisible = this.isPageVisible
    this.isPageVisible = !document.hidden

    if (wasVisible && !this.isPageVisible) {
      // Page became hidden - notify provider to logout
      this.onVisibilityChange?.(false)
    } else if (!wasVisible && this.isPageVisible) {
      // Page became visible - notify provider to reconnect/login
      this.onVisibilityChange?.(true)
      this.onReconnectNeeded?.()
    }
  }

  // Called by provider to set visibility change callback
  setVisibilityCallback(callback: (visible: boolean) => void) {
    this.onVisibilityChange = callback
  }

  // Called by provider to set reconnect callback
  setReconnectCallback(callback: () => void) {
    this.onReconnectNeeded = callback
  }

  // Called by provider when login state changes
  setLoginState(isLoggedIn: boolean) {
    const wasLoggedIn = this.isLoggedIn
    this.isLoggedIn = isLoggedIn

    // If we just logged in, process pending subscriptions
    if (!wasLoggedIn && isLoggedIn) {
      this.processPendingSubscriptions()
    }

    // If we logged out, clear active subscriptions (but keep pending ones)
    if (wasLoggedIn && !isLoggedIn) {
      this.activeSubscriptions.clear()
    }
  }

  private processPendingSubscriptions() {
    if (!this.isLoggedIn) return

    const pending = [...this.pendingSubscriptions]
    this.pendingSubscriptions = []

    pending.forEach(({ eventList, subscribeFn }) => {
      // Check if this subscription is still needed (has active hooks)
      if (this.activeSubscriptions.has(eventList)) {
        subscribeFn()
      }
    })
  }

  subscribe(
    eventList: string,
    hookId: string,
    subscribeFn: SubscriptionHandler,
  ) {
    // Ensure client-side initialization
    if (typeof window !== 'undefined') {
      this.initializeClient()
    }

    // Cancel any pending unsubscription for this event
    if (this.pendingUnsubscriptions.has(eventList)) {
      const timeout = this.pendingUnsubscriptions.get(eventList)
      if (timeout) {
        clearTimeout(timeout)
      }
      this.pendingUnsubscriptions.delete(eventList)
    }

    const isNewSubscription = !this.activeSubscriptions.has(eventList)

    if (isNewSubscription) {
      this.activeSubscriptions.set(eventList, new Set())
    }

    const subscribers = this.activeSubscriptions.get(eventList)
    if (subscribers) {
      subscribers.add(hookId)
    }

    // Only subscribe if page is visible and we're logged in
    if (isNewSubscription) {
      if (!this.isPageVisible || !this.isLoggedIn) {
        // Queue for later
        this.pendingSubscriptions.push({ eventList, subscribeFn })
      } else {
        // Subscribe immediately
        subscribeFn()
      }
    }
  }

  unsubscribe(
    eventList: string,
    hookId: string,
    unsubscribeFn: UnsubscriptionHandler,
  ) {
    const subscribers = this.activeSubscriptions.get(eventList)
    if (!subscribers) return

    subscribers.delete(hookId)

    if (subscribers.size === 0) {
      // Remove from pending subscriptions if it exists
      this.pendingSubscriptions = this.pendingSubscriptions.filter(
        (p) => p.eventList !== eventList,
      )

      // Delay the actual unsubscription to handle rapid re-subscriptions
      const timeout = setTimeout(() => {
        unsubscribeFn()
        this.activeSubscriptions.delete(eventList)
        this.pendingUnsubscriptions.delete(eventList)
      }, UNSUBSCRIBE_DEBOUNCE_MS)

      this.pendingUnsubscriptions.set(eventList, timeout)
    }
  }

  // Get all active subscriptions (for debugging)
  getActiveSubscriptions(): string[] {
    return Array.from(this.activeSubscriptions.keys())
  }

  // Get pending subscriptions count (for debugging)
  getPendingSubscriptionsCount(): number {
    return this.pendingSubscriptions.length
  }

  // Check if a specific subscription is pending
  isSubscriptionPending(eventList: string): boolean {
    return this.pendingSubscriptions.some((p) => p.eventList === eventList)
  }

  // Cleanup method for testing or hot-reload scenarios
  cleanup() {
    if (this.visibilityHandler && typeof window !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
    }
    this.isInitialized = false
    this.activeSubscriptions.clear()
    this.pendingUnsubscriptions.forEach((timeout) => clearTimeout(timeout))
    this.pendingUnsubscriptions.clear()
    this.pendingSubscriptions = []
    this.isLoggedIn = false
  }
}

export const wsSubscriptionManager = new WebSocketSubscriptionManager()
